import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, type Location } from 'react-router-dom';
import { useSignIn } from '@clerk/clerk-react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowRight } from 'lucide-react';
import { AnimateIn, Button, GoogleIcon } from '../../shared/ui';
import { getClerkErrorMessage, matchClerkStatus, recoverCompletedSession } from '../../lib/clerkFlow';
import { AuthField } from './components/AuthField';
import { AuthPasswordField } from './components/AuthPasswordField';
import { AuthOtpField } from './components/AuthOtpField';
import { AuthServerError } from './components/AuthServerError';
import { AuthCaptcha } from './components/AuthCaptcha';
import { AuthSubmitButton } from './components/AuthSubmitButton';
import { usePageMeta } from '../../shared/hooks/usePageMeta';

const schema = z.object({
  email: z.string().email('Enter a valid email'),
  password: z.string().min(1, 'Password is required'),
});

const otpSchema = z.object({
  code: z.string().length(6, 'Enter the 6-digit code'),
});

type FormData = z.infer<typeof schema>;
type OtpData = z.infer<typeof otpSchema>;

const DASHBOARD_PATH = '/app/requests';

export function LoginPage() {
  usePageMeta('Log In');
  const { signIn, setActive, isLoaded } = useSignIn();
  const [step, setStep] = useState<'password' | 'verify'>('password');
  const [serverError, setServerError] = useState('');
  const [googleLoading, setGoogleLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setInterval(() => setResendCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendCooldown]);
  const navigate = useNavigate();
  const location = useLocation();

  const form = useForm<FormData>({ resolver: zodResolver(schema) });
  const otpForm = useForm<OtpData>({ resolver: zodResolver(otpSchema) });

  // A user bounced here by RequireAuth (e.g. hit a deep link while signed out) carries their
  // original destination in router state — honor it instead of always landing on the dashboard.
  const from = (location.state as { from?: Location } | null)?.from;
  const redirectTo = from ? `${from.pathname}${from.search}` : DASHBOARD_PATH;

  const signInWithGoogle = async () => {
    if (!isLoaded || !signIn) return;
    setGoogleLoading(true);
    try {
      await signIn.authenticateWithRedirect({
        strategy: 'oauth_google',
        redirectUrl: `${window.location.origin}/sso-callback`,
        redirectUrlComplete: DASHBOARD_PATH,
      });
    } catch {
      setGoogleLoading(false);
    }
  };

  const completeSignIn = async (sessionId: string) => {
    if (!setActive) return;
    await setActive({ session: sessionId });
    navigate(redirectTo, { replace: true });
  };

  const onSubmit = async ({ email, password }: FormData) => {
    if (!isLoaded || !signIn) return;
    setServerError('');
    try {
      const result = await signIn.create({ identifier: email, password });
      await matchClerkStatus(result, {
        complete: async (r) => {
          await completeSignIn(r.createdSessionId!);
        },
        needs_second_factor: async () => {
          if (result.supportedSecondFactors?.some((f) => f.strategy === 'email_code')) {
            await signIn.prepareSecondFactor({ strategy: 'email_code' });
            setStep('verify');
          } else {
            setServerError('Sign-in could not be completed. Please try again or contact support.');
          }
        },
        fallback: () => {
          setServerError('Sign-in could not be completed. Please try again or contact support.');
        },
      });
    } catch (err: unknown) {
      setServerError(getClerkErrorMessage(err, 'Invalid email or password.'));
    }
  };

  const onVerify = async ({ code }: OtpData) => {
    if (!isLoaded || !signIn) return;
    setServerError('');
    try {
      const result = await signIn.attemptSecondFactor({ strategy: 'email_code', code });
      await matchClerkStatus(result, {
        complete: async (r) => {
          await completeSignIn(r.createdSessionId!);
        },
        fallback: () => {
          setServerError('Sign-in could not be completed. Please try again or contact support.');
        },
      });
    } catch (err: unknown) {
      const sessionId = await recoverCompletedSession(signIn);
      if (sessionId) {
        await completeSignIn(sessionId);
        return;
      }
      setServerError(getClerkErrorMessage(err, 'Invalid or expired code.'));
    }
  };

  const RESEND_COOLDOWN_SECONDS = 30;

  const resendCode = async () => {
    if (!isLoaded || !signIn || resending || resendCooldown > 0) return;
    setResending(true);
    try {
      await signIn.prepareSecondFactor({ strategy: 'email_code' });
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    } catch {
      // best-effort; the user can just try entering a code if one already arrived
    } finally {
      setResending(false);
    }
  };

  if (step === 'verify') {
    return (
      <div className="w-full step-enter">
        <AnimateIn animation="fade-up">
          <h1 className="mb-1.5 text-center text-[clamp(32px,8vw,40px)] font-bold leading-[1.1] tracking-[-0.035em] text-white">Check your email.</h1>
          <p className="mb-8 text-center text-[#a1a1a6]">We sent a 6-digit code to your inbox to finish signing in.</p>
        </AnimateIn>

        <form onSubmit={otpForm.handleSubmit(onVerify)} className="flex flex-col gap-4">
          <Controller
            control={otpForm.control}
            name="code"
            defaultValue=""
            render={({ field }) => (
              <AuthOtpField
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                onComplete={() => otpForm.handleSubmit(onVerify)()}
                error={otpForm.formState.errors.code?.message}
              />
            )}
          />

          <AuthServerError message={serverError} />

          <AuthSubmitButton
            isLoaded={!!isLoaded}
            isSubmitting={otpForm.formState.isSubmitting}
            loadingText="Verifying..."
          >
            Verify & continue <ArrowRight size={15} />
          </AuthSubmitButton>

          <Button
            type="button"
            variant="ghost"
            onClick={resendCode}
            disabled={resending || resendCooldown > 0}
            className="!text-accent !text-xs normal-case !tracking-normal !px-0 hover:!text-accent/70 hover:!bg-transparent self-start disabled:!opacity-50"
          >
            {resending ? 'Sending…' : resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : 'Resend code'}
          </Button>

          <Button
            type="button"
            variant="ghost"
            onClick={() => { setStep('password'); setServerError(''); }}
            className="!text-white/55 !text-xs normal-case !tracking-normal !px-0 hover:!text-white/80 hover:!bg-transparent"
          >
            ← Back
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div className="w-full">
      <AnimateIn animation="fade-up">
        <h1 className="mb-1.5 text-center text-[clamp(32px,8vw,40px)] font-bold leading-[1.1] tracking-[-0.035em] text-white">Sign in.</h1>
        <p className="mb-8 text-center text-[#a1a1a6]">
          No account?{' '}
          <Link to="/signup" className="text-accent hover:underline">
            Sign up free
          </Link>
        </p>
      </AnimateIn>

      <AnimateIn animation="fade-up" delay={100}>
        {/* Google */}
        <Button
          type="button"
          variant="social"
          onClick={signInWithGoogle}
          disabled={!isLoaded}
          loading={googleLoading}
          loadingText="Redirecting..."
          className="mb-5 w-full !rounded-full !border-2 !border-[#424245] !bg-[#f5f5f7] !px-5 !py-[15px] !text-[17px] !font-semibold hover:!bg-white disabled:!opacity-60"
        >
          <GoogleIcon />
          Continue with Google
        </Button>

        {/* Divider */}
        <div className="flex items-center gap-3 mb-5">
          <div className="h-0.5 flex-1 bg-[#333336]" />
          <span className="text-[13px] text-[#6e6e73]">or</span>
          <div className="h-0.5 flex-1 bg-[#333336]" />
        </div>
      </AnimateIn>

      <AnimateIn animation="fade-up" delay={200}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
          <AuthField
            label="Email"
            type="email"
            placeholder="you@company.com"
            autoComplete="email"
            error={form.formState.errors.email?.message}
            {...form.register('email')}
          />

          <AuthPasswordField
            label="Password"
            placeholder="••••••••"
            autoComplete="current-password"
            error={form.formState.errors.password?.message}
            labelExtra={
              <Link to="/forgot-password" className="text-[11px] text-white/30 hover:text-accent transition-colors">
                Forgot password?
              </Link>
            }
            {...form.register('password')}
          />

          <AuthServerError message={serverError} />

          <AuthCaptcha />

          <AuthSubmitButton
            isLoaded={!!isLoaded}
            isSubmitting={form.formState.isSubmitting}
            loadingText="Signing in..."
            className="mt-1"
          >
            Sign in <ArrowRight size={15} />
          </AuthSubmitButton>
        </form>
      </AnimateIn>
    </div>
  );
}
